import { useEffect, useState } from "react";
import { getStats, getSchedule, setMoodEnergy, recentSessions, updateScheduleBlock } from "@/lib/api";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Zap, Timer, Flame, Target, Rocket, Activity, TrendingUp, Pencil, Plus } from "lucide-react";
import WorkSessionDialog from "@/components/WorkSessionDialog";
import ScheduleBlockDialog from "@/components/ScheduleBlockDialog";
import { toast } from "sonner";

function Stat({ label, value, sub, icon: Icon, accent = "text-foreground", testid }) {
  return (
    <Card data-testid={testid} className="bg-card border-border rounded-md h-full">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <span className="overline">{label}</span>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className={`mono text-3xl font-bold mt-3 ${accent}`}>{value}</div>
        {sub && <div className="mono text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [mood, setMood] = useState(7);
  const [energy, setEnergy] = useState(7);
  const [sessions, setSessions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);

  const refresh = async () => {
    const [s, sch, rs] = await Promise.all([getStats(), getSchedule(), recentSessions()]);
    setStats(s); setSchedule(sch.blocks || []);
    setSessions(rs.sessions || []);
    setMood(s.mood ?? 7); setEnergy(s.energy ?? 7);
  };

  useEffect(() => { refresh(); }, []);

  const saveMoodEnergy = async (m, e) => {
    await setMoodEnergy(m, e);
    toast.success("Mood/energy saved");
  };

  const toggleBlockComplete = async (block, completed) => {
    setSchedule((s) => s.map((b) => (b.id === block.id ? { ...b, completed } : b)));
    try {
      await updateScheduleBlock(block.id, { completed });
    } catch {
      toast.error("Could not update block");
      setSchedule((s) => s.map((b) => (b.id === block.id ? { ...b, completed: !completed } : b)));
    }
  };

  const openAddBlock = () => { setEditingBlock(null); setScheduleDialogOpen(true); };
  const openEditBlock = (block) => { setEditingBlock(block); setScheduleDialogOpen(true); };

  if (!stats) {
    return (
      <div className="mono text-sm text-muted-foreground animate-pulse">
        <span className="dot-pulse">Booting Consultant.OS…</span>
      </div>
    );
  }

  const dayPct = Math.min(100, ((stats.todays_hours || 0) / 6) * 100);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Today · Day {stats.day_number}/30</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mt-2">
            <span className="text-primary terminal-glow">$</span> {stats.current_mission}
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Level {stats.current_level} · {stats.completion_pct}% of the roadmap complete. No thinking. Just execute.
          </p>
        </div>
        <Button
          data-testid="start-work-session-btn"
          size="lg"
          onClick={() => setDialogOpen(true)}
          className="bg-primary hover:bg-secondary text-primary-foreground hover:text-white mono uppercase tracking-widest text-xs px-6 py-6 rounded-md border border-primary shadow-[0_0_24px_rgba(57,211,83,0.35)]"
        >
          <Rocket className="h-4 w-4 mr-2" /> Start Work Session
        </Button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat testid="stat-streak" label="Streak" value={`${stats.streak_days}d`} sub="Don't break the chain" icon={Flame} accent="text-primary" />
        <Stat testid="stat-xp" label="XP Total" value={stats.xp} sub={`+${stats.todays_xp} today`} icon={Zap} accent="text-accent" />
        <Stat testid="stat-hours" label="Hours Today" value={stats.todays_hours} sub={`Target: 6h · ${Math.round(dayPct)}%`} icon={Timer} />
        <Stat testid="stat-completion" label="Roadmap" value={`${stats.completion_pct}%`} sub="Complete" icon={Target} accent="text-primary" />
      </div>

      {/* Progress + mood */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-card border-border lg:col-span-2 rounded-md">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <span className="overline">Today's Progress</span>
              <span className="mono text-xs text-muted-foreground">{stats.todays_hours}h / 6h</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={dayPct} className="h-2 bg-muted [&>div]:bg-primary" />
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" className="mono border-border text-muted-foreground">
                <Activity className="h-3 w-3 mr-1" /> {sessions.length} sessions logged
              </Badge>
              <Badge variant="outline" className="mono border-border text-muted-foreground">
                <TrendingUp className="h-3 w-3 mr-1" /> Total {stats.total_hours}h
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border rounded-md">
          <CardHeader className="pb-2"><span className="overline">Mood · Energy</span></CardHeader>
          <CardContent className="space-y-5 pb-6">
            <div>
              <div className="flex justify-between mono text-xs mb-2">
                <span className="text-muted-foreground">Mood</span>
                <span className="text-primary">{mood}/10</span>
              </div>
              <Slider
                data-testid="mood-slider"
                value={[mood]} min={1} max={10} step={1}
                onValueChange={(v) => setMood(v[0])}
                onValueCommit={(v) => saveMoodEnergy(v[0], energy)}
              />
            </div>
            <div>
              <div className="flex justify-between mono text-xs mb-2">
                <span className="text-muted-foreground">Energy</span>
                <span className="text-primary">{energy}/10</span>
              </div>
              <Slider
                data-testid="energy-slider"
                value={[energy]} min={1} max={10} step={1}
                onValueChange={(v) => setEnergy(v[0])}
                onValueCommit={(v) => saveMoodEnergy(mood, v[0])}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="overline">Today's Schedule</span>
          <Button
            size="sm"
            variant="outline"
            data-testid="schedule-add-block-btn"
            onClick={openAddBlock}
            className="border-border mono text-[11px]"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Block
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {schedule.map((b) => (
            <Card
              key={b.id}
              data-testid={`schedule-block-${b.kind}`}
              className={`bg-card rounded-md border transition-colors duration-150 ${
                b.completed ? "border-primary" : "border-border hover:border-primary"
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="mono text-xs text-primary">{b.time}</span>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`schedule-block-edit-${b.id}`}
                      onClick={() => openEditBlock(b)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <Checkbox
                      data-testid={`schedule-block-complete-${b.id}`}
                      checked={b.completed}
                      onCheckedChange={(v) => toggleBlockComplete(b, Boolean(v))}
                    />
                  </div>
                </div>
                <div className={`mono text-lg mt-2 ${b.completed ? "line-through text-muted-foreground" : ""}`}>{b.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{b.hint}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent sessions */}
      {sessions.length > 0 && (
        <div>
          <div className="overline mb-3">Recent Sessions</div>
          <div className="border border-border rounded-md divide-y divide-border bg-card">
            {sessions.slice(0, 6).map((s) => (
              <div key={s.id} className="grid grid-cols-12 gap-2 items-center px-4 py-3 text-sm">
                <span className="mono text-xs text-muted-foreground col-span-3">{new Date(s.ended_at).toLocaleString()}</span>
                <span className="mono text-xs text-primary col-span-2">{s.work_type}</span>
                <span className="col-span-5 truncate">{s.accomplished}</span>
                <span className="mono text-xs text-right col-span-1">{s.hours}h</span>
                <span className="mono text-xs text-right text-accent col-span-1">+{s.xp_earned}xp</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <WorkSessionDialog open={dialogOpen} onOpenChange={setDialogOpen} onComplete={refresh} />
      <ScheduleBlockDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        block={editingBlock}
        onSaved={refresh}
      />
    </div>
  );
}
